import React, { useState, useEffect, useMemo } from 'react';

/**
 * Page publique de choix de créneau — erpia.ch/entretien/:token
 *
 * Le candidat n'a pas de compte, ouvre le lien depuis son téléphone, choisit
 * une heure, et c'est fini. Pas de calendrier mensuel : il n'y a qu'une
 * poignée de jours, et voir un mois vide n'aide personne. Les créneaux sont
 * groupés par jour, dans l'ordre de lecture.
 *
 * LE FUSEAU EST LE SEUL PIÈGE. Le serveur envoie des instants en UTC ; ils
 * sont affichés dans l'heure du candidat, et cette heure est NOMMÉE. Sans
 * ça, quelqu'un retient 14 h et se présente à 15 h.
 *
 * Routes SANS auth :
 *   GET  /public/entretien/:token → { nom, organisation, poste?, duree_min,
 *                                     creneaux: [{ debut, fin }], fuseau }
 *                                   409 si déjà confirmé (+ creneau)
 *   POST /public/entretien/:token → { debut }
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;

const FUSEAU_LOCAL = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
})();
const heure = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};
const jourLong = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' });
};
const jourCle = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

export default function PublicEntretien({ token }) {
  const [etat, setEtat] = useState('chargement');   // chargement | pret | pris | invalide | confirme
  const [data, setData] = useState(null);
  const [choisi, setChoisi] = useState('');
  const [erreur, setErreur] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/public/entretien/${encodeURIComponent(token)}`);
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 409) { setData(j.data || j); setEtat('pris'); return; }
        if (!r.ok) { setErreur(j.error || j.message || ''); setEtat('invalide'); return; }
        const d = j.data || j;
        const creneaux = Array.isArray(d.creneaux) ? d.creneaux.filter(c => c && c.debut) : [];
        if (!creneaux.length) { setErreur(j.message || ''); setEtat('invalide'); return; }
        setData({ ...d, creneaux });
        setEtat('pret');
      } catch { if (!annule) setEtat('invalide'); }
    })();
    return () => { annule = true; };
  }, [token]);

  // Groupés par jour, dans l'ordre : c'est ainsi qu'on choisit une heure.
  const jours = useMemo(() => {
    const parJour = new Map();
    (data?.creneaux || []).forEach((c) => {
      const k = jourCle(c.debut);
      if (!k) return;
      if (!parJour.has(k)) parJour.set(k, { libelle: jourLong(c.debut), creneaux: [] });
      parJour.get(k).creneaux.push(c);
    });
    return [...parJour.values()];
  }, [data]);

  const confirmer = async () => {
    if (!choisi) return;
    setBusy(true); setErreur('');
    try {
      const r = await fetch(`${BASE}/public/entretien/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ debut: choisi }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { setData(d => ({ ...d, ...(j.data || j) })); setEtat('pris'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || 'Ce créneau n\'a pas pu être retenu.');
      setData(d => ({ ...d, message: j.message, creneau: (j.data || j).creneau || { debut: choisi } }));
      setEtat('confirme');
    } catch (e) { setErreur(e.message); }
    finally { setBusy(false); }
  };

  const org = data?.organisation || '';
  const cadre = (icone, titre, corps) => (
    <main className="pubrdv">
      <div className="pubrdv-carte pubrdv-carte--message">
        <BI icon={icone} />
        <h1>{titre}</h1>
        {corps}
      </div>
    </main>
  );
  const quand = (c) => (c?.debut ? `${jourLong(c.debut)} à ${heure(c.debut)}` : null);

  if (etat === 'chargement') return cadre('arrow-repeat', 'Ouverture…', <p>Un instant.</p>);
  if (etat === 'invalide') return cadre('calendar-x', 'Ce lien n\'est plus valable', (
    <p>{erreur && <><b>{erreur}</b><br /></>}Les créneaux proposés ont peut-être expiré. Répondez au message qui vous a envoyé ce lien pour en recevoir de nouveaux.</p>
  ));
  if (etat === 'pris') return cadre('calendar-check', 'C\'est déjà réservé', (
    <>
      <p>{data?.message || 'Un créneau a déjà été retenu pour cet entretien.'}</p>
      {quand(data?.creneau) && <p className="pubrdv-quand">{quand(data.creneau)}{FUSEAU_LOCAL ? ` · heure de ${FUSEAU_LOCAL.split('/').pop().replace(/_/g, ' ')}` : ''}</p>}
    </>
  ));
  if (etat === 'confirme') return cadre('check-circle-fill', 'C\'est noté', (
    <>
      <p className="pubrdv-quand">{quand(data?.creneau)}</p>
      {FUSEAU_LOCAL && <p className="pubrdv-fuseau">Heure de {FUSEAU_LOCAL.split('/').pop().replace(/_/g, ' ')}</p>}
      <p>{data?.message || `${org ? `${org} vous` : 'Vous'} recevrez une confirmation par email.`}</p>
    </>
  ));

  return (
    <main className="pubrdv">
      <div className="pubrdv-carte">
        <header className="pubrdv-tete">
          {org && <p className="pubrdv-kicker">{org}</p>}
          <h1>{data.nom || 'Choisissez votre créneau'}</h1>
          <p>
            {data.poste ? <>Entretien pour le poste de <strong>{data.poste}</strong>. </> : null}
            {data.duree_min ? `Comptez ${data.duree_min} minutes. ` : ''}
            Choisissez l'heure qui vous convient.
          </p>
          {/* Le fuseau, dit avant le choix : c'est la seule chose qui peut
              faire arriver quelqu'un avec une heure d'écart. */}
          {FUSEAU_LOCAL && (
            <p className="pubrdv-fuseau"><BI icon="globe" /> Heures affichées dans votre fuseau — {FUSEAU_LOCAL.replace(/_/g, ' ')}</p>
          )}
        </header>

        {erreur && <div className="pubrdv-erreur" role="alert">{erreur}</div>}

        <div className="pubrdv-jours">
          {jours.map((j) => (
            <section className="pubrdv-jour" key={j.libelle}>
              <h2>{j.libelle}</h2>
              <div className="pubrdv-creneaux" role="radiogroup" aria-label={`Créneaux du ${j.libelle}`}>
                {j.creneaux.map((c) => (
                  <button type="button" key={c.debut} role="radio" aria-checked={choisi === c.debut}
                    className={`pubrdv-creneau${choisi === c.debut ? ' is-choisi' : ''}`}
                    onClick={() => setChoisi(c.debut)}>
                    {heure(c.debut)}
                    {c.fin && <small>→ {heure(c.fin)}</small>}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className="pubrdv-pied">
        <span className="pubrdv-choix">
          {choisi ? quand({ debut: choisi }) : 'Aucun créneau sélectionné.'}
        </span>
        <button type="button" className="pubrdv-valider" onClick={confirmer} disabled={busy || !choisi}>
          {busy ? 'Confirmation…' : 'Confirmer ce créneau'}
        </button>
      </div>
    </main>
  );
}
